import { Formik, useField } from "formik";
import { useState } from "react";
import { Icon } from "@iconify/react";

export const TextInput = ({ label, required, ...props }) => {
  const [field, meta] = useField(props);
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="mb-4 w-full">
      <label
        htmlFor={props.id || props.name}
        className="block text-sm font-medium text-gray-700"
      >
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      {props.type === "password" ? (
        <div className="relative">
          <input
            {...field}
            {...props}
            type={showPassword ? "text" : "password"}
            className={`mt-1 block form-control py-2 h-[45px] w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 ${
              props.className || ""
            } ${meta.touched && meta.error ? "border-red-500" : ""}`}
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute inset-y-5 right-0 pr-3 flex items-center text-sm leading-5"
          >
            {showPassword ? (
              <Icon icon="heroicons-outline:eye-slash" />
            ) : (
              <Icon icon="heroicons-outline:eye" />
            )}
          </button>
        </div>
      ) : (
        <input
          {...field}
          {...props}
          className={`mt-1 block form-control py-2 h-[45px] w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 ${
            props.className || ""
          } ${meta.touched && meta.error ? "border-red-500" : ""}`}
        />
      )}
      {meta.touched && meta.error ? (
        <div className="text-sm text-red-500 mt-2">{meta.error}</div>
      ) : null}
    </div>
  );
};

export const SelectInput = ({ label, options, required, ...props }) => {
  const [field, meta] = useField(props);

  return (
    <div className="mb-4 w-full">
      <label
        htmlFor={props.id || props.name}
        className="block text-sm font-medium text-gray-700"
      >
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <select
        {...field}
        {...props}
        className={`mt-1 block w-full form-control px-2 py-2 h-[45px] rounded-md border border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 ${
          props.className || ""
        } ${meta.touched && meta.error ? "border-red-500" : ""}`}
      >
        <option value="">Select an option</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {meta.touched && meta.error ? (
        <div className="text-sm text-red-500 mt-2">{meta.error}</div>
      ) : null}
    </div>
  );
};

export const ImageUploadInput = ({
  name,
  label,
  setFieldValue,
  initialValue,
  previewPosition = "below", // 'below' or 'right'
}) => {
  const [imagePreview, setImagePreview] = useState(initialValue || "");
  const [selectedFileName, setSelectedFileName] = useState("");

  const handleImageChange = (e) => {
    const file = e.currentTarget.files[0];

    if (file) {
      // Validate file size
      if (file.size > 2 * 1024 * 1024) {
        alert("File size exceeds 2MB limit. Please select a smaller file.");
        e.target.value = ""; // Reset input
        return;
      }

      // Validate file format
      const allowedFormats = ["jpg", "jpeg", "png", "gif", "webp"];
      const fileFormat = file.name.split(".").pop().toLowerCase();
      if (!allowedFormats.includes(fileFormat)) {
        alert(
          `Invalid file format. Allowed formats: ${allowedFormats.join(", ")}`
        );
        e.target.value = ""; // Reset input
        return;
      }

      setSelectedFileName(file.name);
      setFieldValue(name, file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
      setSelectedFileName("");
      setFieldValue(name, null);
    }
  };

  return (
    <div
      className={`flex ${
        previewPosition === "right" ? "flex-row" : "flex-col"
      } mb-4`}
    >
      <div className="flex-1">
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {label}
          </label>
        )}
        <input
          name={name}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
        {selectedFileName && (
          <div className="mt-2 flex items-center text-sm text-gray-600">
            <span className="truncate">Selected: {selectedFileName}</span>
          </div>
        )}
        {imagePreview && (
          <div className="mt-2 text-xs text-blue-600">
            ✓ Image selected and ready for upload
          </div>
        )}
      </div>
      {imagePreview && (
        <div className={`${previewPosition === "right" ? "ml-4" : "mt-2"}`}>
          <div className="relative inline-block">
            <img
              src={imagePreview}
              alt="Preview"
              className="w-32 h-32 object-cover rounded-md border-2 border-gray-200"
            />
            <div className="absolute top-1 right-1 bg-blue-500 rounded-full p-1">
              <Icon
                icon="heroicons-outline:check"
                className="text-white text-xs"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
